import React, {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";

import { isEmpty, isEqual, omit } from "lodash";

import { LoadingSlide } from "@components/title-slide-view";
import { useLogger } from "@components/v2/analytics";
import {
  FETCH_CREATIVE_SAVED_VIEWS,
  UPSERT_CREATIVE_SAVED_VIEWS,
  UPSERT_CREATIVE_SAVED_VIEWS_ORDERING_TO_ALL_ACCOUNT_MANAGERS,
} from "@gql/creative-analytics";
import { useNorthbeamMutation, useNorthbeamQuery } from "@hooks/http";
import {
  type FetchCreativeSavedViews,
  type FetchCreativeSavedViews_me_creativeSavedViews_views,
  type FetchCreativeSavedViews_me_creativeSavedViews,
  type UpsertCreativeAnalyticsSavedViews,
  type UpsertCreativeAnalyticsSavedViewsVariables,
  type UpsertCreativeAnalyticsSavedViewsOrderingToAllAccountManagers,
  type UpsertCreativeAnalyticsSavedViewsOrderingToAllAccountManagersVariables,
} from "@north-beam/nb-client-side";
import { type AmplitudeNativeEventType } from "@north-beam/nb-client-side/analytics";
import { parseInteger } from "@north-beam/nb-common/parsers";

import { type ReportBodyState, useReportBodyState } from "./ca-control-state";

interface SalesViewContextType {
  availableSavedViews: FetchCreativeSavedViews_me_creativeSavedViews["views"];
  createNewSavedView: (name: string) => Promise<void>;
  /* If the selection of the saved view was performed via a user action, the event option should
     be defined.  This will trigger the event logging to occur. */
  setSelectedSaveView: (
    newSelectedSavedViewId: string,
    options?: { event: AmplitudeNativeEventType },
  ) => void;
  hasUnsavedChanges: boolean;
  selectedSavedView: FetchCreativeSavedViews_me_creativeSavedViews_views;
  loadingSavedViewUpsert: boolean;
  upsertSavedViewOrderingToAllAccountManagers: (
    views: UpsertCreativeAnalyticsSavedViewsVariables["views"],
  ) => Promise<void>;
  saveAllViews: (
    views: UpsertCreativeAnalyticsSavedViewsVariables["views"],
  ) => Promise<void>;
}
const SalesViewContext = createContext<SalesViewContextType>(
  {} as SalesViewContextType,
);

export const SalesViewProvider = ({ children }: { children: ReactNode }) => {
  const state = useReportBodyState();
  const logger = useLogger({});

  const { loading, data } = useNorthbeamQuery<FetchCreativeSavedViews>(
    FETCH_CREATIVE_SAVED_VIEWS,
  );

  const [upsertSavedView, { loading: loadingSavedViewUpsert }] =
    useNorthbeamMutation<
      UpsertCreativeAnalyticsSavedViews,
      UpsertCreativeAnalyticsSavedViewsVariables
    >(UPSERT_CREATIVE_SAVED_VIEWS, {
      refetchQueries: [{ query: FETCH_CREATIVE_SAVED_VIEWS, variables: {} }],
    });

  const [
    upsertSavedViewOrderingToAllAccountManagersMutation,
    { loading: loadingSavedViewOrderingToAllAccountManagers },
  ] = useNorthbeamMutation<
    UpsertCreativeAnalyticsSavedViewsOrderingToAllAccountManagers,
    UpsertCreativeAnalyticsSavedViewsOrderingToAllAccountManagersVariables
  >(UPSERT_CREATIVE_SAVED_VIEWS_ORDERING_TO_ALL_ACCOUNT_MANAGERS, {
    refetchQueries: [{ query: FETCH_CREATIVE_SAVED_VIEWS, variables: {} }],
  });
  const availableSavedViews = data?.me.creativeSavedViews.views;

  const [selectedSavedView, _setSelectedSaveView] =
    useState<FetchCreativeSavedViews_me_creativeSavedViews_views>();

  const [hasUnsavedChanges, setUnsavedChanges] = useState(false);

  const upsertSavedViewOrderingToAllAccountManagers = useCallback(
    async (views: UpsertCreativeAnalyticsSavedViewsVariables["views"]) => {
      await upsertSavedViewOrderingToAllAccountManagersMutation({
        variables: { ids: views?.map((v) => Number(v.id)) },
      });
    },
    [upsertSavedViewOrderingToAllAccountManagersMutation],
  );

  // this solution is for "on mount"
  useEffect(() => {
    if (!data?.me?.creativeSavedViews.views) {
      return;
    }
    if (selectedSavedView?.id) {
      return;
    }
    const savedViews = data.me.creativeSavedViews.views;
    if (state.selectedSaveViewId) {
      return _setSelectedSaveView(
        savedViews.find(({ id }) => id === state.selectedSaveViewId),
      );
    }

    const selectedView = savedViews[0];
    _setSelectedSaveView(selectedView);

    const selectedViewConfig = selectedView.config;

    state.setState({
      ...omit(selectedViewConfig, ["__typename"]),
      searchText:
        state.searchText || (selectedViewConfig.searchText ?? undefined),
      breakdownFilters: selectedViewConfig.breakdownFilters ?? [],
      selectedSaveViewId: selectedView.id,
      dateRange: selectedViewConfig.dateRange.details,
      activeStatus: selectedViewConfig.status,
      attributionWindow:
        selectedViewConfig.attributionWindow as ReportBodyState["attributionWindow"],
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.me.creativeSavedViews.views, selectedSavedView?.id]);

  useEffect(() => {
    if (!data?.me?.creativeSavedViews.views) {
      return;
    }
    // there is always guranteed to be at least one because we have default saved views
    const selectedView =
      selectedSavedView ?? data.me.creativeSavedViews.views[0]!;

    const savedViewConfig = selectedView.config;

    if (
      isEqual(savedViewConfig.attributionModel, state.attributionModel) &&
      isEqual(savedViewConfig.attributionWindow, state.attributionWindow) &&
      isEqual(savedViewConfig.dateRange.details, state.unparsedDateRange) &&
      isEqual(savedViewConfig.groupByAdName, state.groupAdsByName) &&
      isEqual(savedViewConfig.heroMetric, state.heroMetric) &&
      isEqual(savedViewConfig.sorting, state.sorting) &&
      isEqual(savedViewConfig.sortingOrder, state.sortingOrder) &&
      isEqual(savedViewConfig.status, state.activeStatus) &&
      isEqual(savedViewConfig.creativeType, state.creativeType) &&
      isEqual(
        savedViewConfig.platforms.slice().sort(),
        state.selectedPlatforms.slice().sort(),
      ) &&
      isEqual(
        savedViewConfig.metrics.slice().sort(),
        [...state.metrics].slice().sort(),
      ) &&
      isEqual(savedViewConfig.searchText ?? "", state.searchText) &&
      isEqual(savedViewConfig.metricFilters ?? {}, state.metricFilters) &&
      isEqual(savedViewConfig.breakdownFilters ?? [], state.breakdownFilters)
    ) {
      setUnsavedChanges(false);
    } else {
      setUnsavedChanges(true);
    }
  }, [
    data,
    selectedSavedView,
    state.attributionModel,
    state.attributionWindow,
    state.unparsedDateRange,
    state.groupAdsByName,
    state.selectedPlatforms,
    state.metrics,
    state.heroMetric,
    state.sorting,
    state.sortingOrder,
    state.activeStatus,
    state.creativeType,
    state.metricFilters,
    state.searchText,
    state.breakdownFilters,
  ]);

  const setSelectedSaveView = useCallback(
    (newSavedViewId: string, options?: { event: AmplitudeNativeEventType }) => {
      const newSelectedSavedView = availableSavedViews?.find(
        (view) => view.id === newSavedViewId,
      );
      if (!newSelectedSavedView) {
        return;
      }
      _setSelectedSaveView(newSelectedSavedView);
      if (options?.event) {
        logger.event.selectCreativeAnalyticsSavedView(options.event, {
          container: "saved-views-menu",
          eventDetails: {
            savedView: {
              id: parseInteger(newSelectedSavedView.id, {}),
              name: newSelectedSavedView.name,
              isTemplate: false,
            },
          },
        });
      }
      const newSelectedSavedViewConfig = newSelectedSavedView.config;

      state.setState({
        ...omit(newSelectedSavedViewConfig, "__typename"),
        searchText: newSelectedSavedViewConfig.searchText ?? undefined,
        breakdownFilters: newSelectedSavedViewConfig.breakdownFilters ?? [],
        selectedSaveViewId: newSelectedSavedView.id,
        activeStatus: newSelectedSavedViewConfig.status,
        dateRange: newSelectedSavedViewConfig.dateRange.details,
        attributionWindow:
          newSelectedSavedViewConfig.attributionWindow as ReportBodyState["attributionWindow"],
      });
    },
    [availableSavedViews, state, logger],
  );

  const saveAllViews = useCallback(
    async (views: UpsertCreativeAnalyticsSavedViewsVariables["views"]) => {
      await upsertSavedView({
        variables: {
          views: [
            ...(views.map((v) => ({
              ...omit(v, "__typename"),
              config: omit(v.config, "__typename"),
            })) ?? []),
          ],
        },
      });
    },
    [upsertSavedView],
  );

  const createNewSavedView = useCallback(
    async (name: string) => {
      await upsertSavedView({
        variables: {
          views: [
            ...(data?.me.creativeSavedViews.views.map((v) => ({
              ...omit(v, "__typename"),
              config: omit(v.config, "__typename"),
            })) ?? []),
            {
              isDefault: false,
              name,
              config: {
                attributionModel: state.attributionModel,
                attributionWindow: state.attributionWindow,
                dateRange: { details: state.unparsedDateRange },
                groupByAdName: state.groupAdsByName,
                heroMetric: state.heroMetric,
                platforms: [...state.selectedPlatforms],
                metrics: [...state.metrics],
                sorting: state.sorting,
                sortingOrder: state.sortingOrder,
                status: state.activeStatus,
                creativeType: state.creativeType,
                searchText: state.searchText,
                metricFilters: isEmpty(state.metricFilters)
                  ? undefined
                  : state.metricFilters,
                breakdownFilters: isEmpty(state.breakdownFilters)
                  ? undefined
                  : state.breakdownFilters,
              },
            },
          ],
        },
      });
    },
    [
      upsertSavedView,
      data?.me.creativeSavedViews.views,
      state.attributionModel,
      state.attributionWindow,
      state.unparsedDateRange,
      state.groupAdsByName,
      state.heroMetric,
      state.selectedPlatforms,
      state.metrics,
      state.sorting,
      state.sortingOrder,
      state.activeStatus,
      state.creativeType,
      state.searchText,
      state.metricFilters,
      state.breakdownFilters,
    ],
  );

  if (loading || !data || !selectedSavedView) {
    return <LoadingSlide />;
  }

  return (
    <SalesViewContext.Provider
      value={{
        saveAllViews,
        availableSavedViews: data.me.creativeSavedViews.views,
        upsertSavedViewOrderingToAllAccountManagers,
        hasUnsavedChanges,
        setSelectedSaveView,
        selectedSavedView,
        createNewSavedView,
        loadingSavedViewUpsert:
          loadingSavedViewUpsert ||
          loadingSavedViewOrderingToAllAccountManagers,
      }}
    >
      {children}
    </SalesViewContext.Provider>
  );
};

export const useSavedViews = () => {
  const context = useContext(SalesViewContext);
  if (context === undefined) {
    throw new Error("useSavedViews must be used within a SalesViewsProvider");
  }
  return context;
};
